/**
 * LeadsDashboard - Vista simple de solicitudes de contacto
 * Solo muestra las solicitudes recibidas, sin gestión de estados
 */

import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  useLeads,
  Lead,
  formatearFechaLead,
  getTipoContactoLabel
} from '../hooks/useLeads'

interface LeadsDashboardProps {
  asesorId: string | string[]
  variant?: 'widget' | 'full'
  maxItems?: number
}

// Tipos de filtro
type TipoContactoFiltro = 'todos' | 'informacion' | 'visita' | 'oferta' | 'otro'
type PeriodoFiltro = 'todos' | 'hoy' | 'semana' | 'mes'

export default function LeadsDashboard({
  asesorId,
  variant = 'widget',
  maxItems = 10
}: LeadsDashboardProps) {
  const { leads, loading, error, totalLeads, refetch } = useLeads({
    asesorId,
    limitResults: variant === 'widget' ? 5 : 100 // Cargar más para filtrar en cliente
  })

  const [expandedLead, setExpandedLead] = useState<string | null>(null)

  // Estados de filtros (solo para vista full)
  const [filtroTipo, setFiltroTipo] = useState<TipoContactoFiltro>('todos')
  const [filtroPeriodo, setFiltroPeriodo] = useState<PeriodoFiltro>('todos')
  const [busqueda, setBusqueda] = useState('')
  const [vista, setVista] = useState<'lista' | 'propiedad'>('lista')
  const [expandedPropiedad, setExpandedPropiedad] = useState<string | null>(null)

  // Filtrar leads
  const leadsFiltrados = useMemo(() => {
    if (variant === 'widget') return leads.slice(0, 5)

    return leads.filter(lead => {
      // Filtro por tipo de contacto
      if (filtroTipo !== 'todos' && lead.tipoContacto !== filtroTipo) {
        return false
      }

      // Filtro por período
      if (filtroPeriodo !== 'todos' && lead.fechaCreacion?.toDate) {
        const fechaLead = lead.fechaCreacion.toDate()
        const ahora = new Date()

        if (filtroPeriodo === 'hoy') {
          const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate())
          if (fechaLead < inicioHoy) return false
        } else if (filtroPeriodo === 'semana') {
          const hace7dias = new Date(ahora.getTime() - 7 * 24 * 60 * 60 * 1000)
          if (fechaLead < hace7dias) return false
        } else if (filtroPeriodo === 'mes') {
          const hace30dias = new Date(ahora.getTime() - 30 * 24 * 60 * 60 * 1000)
          if (fechaLead < hace30dias) return false
        }
      }

      // Filtro por búsqueda
      if (busqueda.trim()) {
        const searchLower = busqueda.toLowerCase()
        const matchNombre = lead.nombre?.toLowerCase().includes(searchLower)
        const matchPropiedad = lead.propertyName?.toLowerCase().includes(searchLower)
        const matchEmail = lead.email?.toLowerCase().includes(searchLower)
        if (!matchNombre && !matchPropiedad && !matchEmail) return false
      }

      return true
    }).slice(0, maxItems)
  }, [leads, filtroTipo, filtroPeriodo, busqueda, variant, maxItems])

  // Agrupar leads por propiedad; cada grupo ordenado internamente y los
  // grupos ordenados por fecha del lead más reciente (descendente).
  const gruposPorPropiedad = useMemo(() => {
    const mapa = new Map<string, { propertyId: string; propertyName: string; leads: Lead[]; ultimaFecha: number }>()
    for (const lead of leadsFiltrados) {
      const key = lead.propertyId || 'sin-propiedad'
      const existing = mapa.get(key)
      const fechaMs = lead.fechaCreacion?.toDate?.().getTime() || 0
      if (existing) {
        existing.leads.push(lead)
        if (fechaMs > existing.ultimaFecha) existing.ultimaFecha = fechaMs
      } else {
        mapa.set(key, {
          propertyId: lead.propertyId || '',
          propertyName: lead.propertyName || 'Propiedad sin nombre',
          leads: [lead],
          ultimaFecha: fechaMs,
        })
      }
    }
    // Orden interno de leads por fecha descendente
    mapa.forEach(g => {
      g.leads.sort((a, b) => {
        const fa = a.fechaCreacion?.toDate?.().getTime() || 0
        const fb = b.fechaCreacion?.toDate?.().getTime() || 0
        return fb - fa
      })
    })
    return Array.from(mapa.values()).sort((a, b) => b.ultimaFecha - a.ultimaFecha)
  }, [leadsFiltrados])

  // Widget compacto para el dashboard
  if (variant === 'widget') {
    return (
      <div className="card border-0 shadow-sm h-100">
        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="fa-solid fa-envelope text-primary me-2"></i>
            Solicitudes Recibidas
            {totalLeads > 0 && (
              <span className="badge bg-primary ms-2">{totalLeads}</span>
            )}
          </h6>
          <Link to="/mis-leads" className="btn btn-sm btn-outline-primary">
            Ver todas
          </Link>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="small text-muted mt-2 mb-0">Cargando solicitudes...</p>
            </div>
          ) : error ? (
            <div className="text-center text-danger py-4">
              <i className="fa-solid fa-triangle-exclamation fa-2x mb-2 d-block"></i>
              <small>{error}</small>
              <button className="btn btn-sm btn-outline-primary d-block mx-auto mt-2" onClick={refetch}>
                Reintentar
              </button>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="fa-regular fa-envelope fa-2x mb-2 d-block"></i>
              <small>No tienes solicitudes</small>
            </div>
          ) : (
            <ul className="list-group list-group-flush">
              {leads.map(lead => (
                <li
                  key={lead.id}
                  className="list-group-item d-flex justify-content-between align-items-center py-3"
                >
                  <div>
                    <div className="fw-semibold">{lead.nombre}</div>
                    <small className="text-muted d-block">{lead.propertyName?.substring(0, 30)}...</small>
                    <span className="badge bg-light text-dark mt-1">
                      {getTipoContactoLabel(lead.tipoContacto)}
                    </span>
                  </div>
                  <small className="text-muted">{formatearFechaLead(lead.fechaCreacion)}</small>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    )
  }

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltroTipo('todos')
    setFiltroPeriodo('todos')
    setBusqueda('')
  }

  const hayFiltrosActivos = filtroTipo !== 'todos' || filtroPeriodo !== 'todos' || busqueda.trim() !== ''

  // Card de un lead individual. hideProperty oculta el link a la propiedad
  // cuando el lead ya está dentro de un grupo-propiedad (evita redundancia).
  const renderLeadCard = (lead: Lead, opts: { hideProperty: boolean }) => (
    <div
      key={lead.id}
      className="list-group-item list-group-item-action"
    >
      <div
        className="d-flex justify-content-between align-items-start"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
      >
        <div className="d-flex align-items-start">
          <div className="me-3">
            <span className="badge bg-primary rounded-circle p-2">
              <i className="fa-solid fa-user"></i>
            </span>
          </div>
          <div>
            <h6 className="mb-1">{lead.nombre}</h6>
            {!opts.hideProperty && (
              <p className="text-muted small mb-1">
                <i className="fa-solid fa-home me-1"></i>
                <Link
                  to={`/single-property-1/${lead.propertyId}`}
                  className="text-decoration-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  {lead.propertyName}
                </Link>
              </p>
            )}
            <span className="badge bg-light text-dark">
              {getTipoContactoLabel(lead.tipoContacto)}
            </span>
          </div>
        </div>
        <div className="text-end">
          <small className="text-muted d-block">{formatearFechaLead(lead.fechaCreacion)}</small>
          <i className={`fa-solid fa-chevron-${expandedLead === lead.id ? 'up' : 'down'} text-muted mt-2`}></i>
        </div>
      </div>

      {expandedLead === lead.id && (
        <div className="mt-3 pt-3 border-top">
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <i className="fa-solid fa-envelope text-primary me-2"></i>
                <a href={`mailto:${lead.email}`}>{lead.email}</a>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <i className="fa-solid fa-phone text-primary me-2"></i>
                <a href={`tel:${lead.telefono}`}>{lead.telefono}</a>
              </div>
            </div>
            <div className="col-md-4">
              <a
                href={`https://wa.me/${lead.telefono.replace(/\D/g, '')}?text=Hola ${lead.nombre}, me contacto por tu interés en ${lead.propertyName}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-success"
              >
                <i className="fa-brands fa-whatsapp me-1"></i>
                WhatsApp
              </a>
            </div>
          </div>

          <div className="bg-light rounded p-3">
            <small className="text-muted d-block mb-1">Mensaje:</small>
            <p className="mb-0">{lead.mensaje}</p>
          </div>
        </div>
      )}
    </div>
  )

  // Vista completa
  return (
    <div className="leads-list">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <span className="text-muted">
            {leadsFiltrados.length} de {totalLeads} solicitud{totalLeads !== 1 ? 'es' : ''}
            {vista === 'propiedad' && gruposPorPropiedad.length > 0 && (
              <span className="text-muted ms-2">· {gruposPorPropiedad.length} propiedad{gruposPorPropiedad.length !== 1 ? 'es' : ''}</span>
            )}
          </span>
        </div>
        <div className="d-flex gap-2">
          <div className="btn-group" role="group" aria-label="Vista">
            <button
              className={`btn btn-sm ${vista === 'lista' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setVista('lista')}
            >
              <i className="fa-solid fa-list me-1"></i>
              Lista
            </button>
            <button
              className={`btn btn-sm ${vista === 'propiedad' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setVista('propiedad')}
            >
              <i className="fa-solid fa-house me-1"></i>
              Por propiedad
            </button>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={refetch}>
            <i className="fa-solid fa-refresh me-1"></i>
            Actualizar
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            {/* Búsqueda */}
            <div className="col-md-4">
              <label className="form-label small text-muted">Buscar</label>
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="fa-solid fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre, email o propiedad..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
            </div>

            {/* Tipo de contacto */}
            <div className="col-md-3">
              <label className="form-label small text-muted">Tipo de solicitud</label>
              <select
                className="form-select"
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value as TipoContactoFiltro)}
              >
                <option value="todos">Todos los tipos</option>
                <option value="informacion">Información</option>
                <option value="visita">Agendar visita</option>
                <option value="oferta">Oferta</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            {/* Período */}
            <div className="col-md-3">
              <label className="form-label small text-muted">Período</label>
              <select
                className="form-select"
                value={filtroPeriodo}
                onChange={(e) => setFiltroPeriodo(e.target.value as PeriodoFiltro)}
              >
                <option value="todos">Todo el tiempo</option>
                <option value="hoy">Hoy</option>
                <option value="semana">Últimos 7 días</option>
                <option value="mes">Últimos 30 días</option>
              </select>
            </div>

            {/* Botón limpiar */}
            <div className="col-md-2">
              {hayFiltrosActivos && (
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={limpiarFiltros}
                >
                  <i className="fa-solid fa-times me-1"></i>
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fa-solid fa-triangle-exclamation me-2"></i>
          {error}
          <button className="btn btn-sm btn-outline-danger ms-auto" onClick={refetch}>
            Reintentar
          </button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted mt-3">Cargando solicitudes...</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-5">
          <i className="fa-regular fa-envelope fa-4x text-muted mb-3"></i>
          <h5 className="text-muted">Sin solicitudes</h5>
          <p className="text-muted">
            Las solicitudes de contacto de tus propiedades aparecerán aquí.
          </p>
        </div>
      ) : leadsFiltrados.length === 0 ? (
        <div className="text-center py-5">
          <i className="fa-solid fa-filter-circle-xmark fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">Sin resultados</h5>
          <p className="text-muted mb-3">
            No hay solicitudes que coincidan con los filtros seleccionados.
          </p>
          <button className="btn btn-outline-primary" onClick={limpiarFiltros}>
            <i className="fa-solid fa-times me-1"></i>
            Limpiar filtros
          </button>
        </div>
      ) : vista === 'lista' ? (
        <div className="list-group">
          {leadsFiltrados.map(lead => renderLeadCard(lead, { hideProperty: false }))}
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {gruposPorPropiedad.map(grupo => {
            const abierto = expandedPropiedad === grupo.propertyId
            return (
              <div key={grupo.propertyId || 'sin-propiedad'} className="card border-0 shadow-sm">
                <div
                  className="card-header bg-white d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExpandedPropiedad(abierto ? null : grupo.propertyId)}
                >
                  <div className="d-flex align-items-center">
                    <i className="fa-solid fa-house text-primary me-2"></i>
                    <div>
                      <h6 className="mb-0">{grupo.propertyName}</h6>
                      <small className="text-muted">
                        Último lead: {formatearFechaLead(grupo.leads[0].fechaCreacion)}
                      </small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-primary rounded-pill">
                      {grupo.leads.length} lead{grupo.leads.length !== 1 ? 's' : ''}
                    </span>
                    {grupo.propertyId && (
                      <Link
                        to={`/single-property-1/${grupo.propertyId}`}
                        className="btn btn-sm btn-outline-secondary"
                        onClick={(e) => e.stopPropagation()}
                        title="Ver propiedad"
                      >
                        <i className="fa-solid fa-eye"></i>
                      </Link>
                    )}
                    <i className={`fa-solid fa-chevron-${abierto ? 'up' : 'down'} text-muted`}></i>
                  </div>
                </div>
                {abierto && (
                  <div className="card-body p-0">
                    <div className="list-group list-group-flush">
                      {grupo.leads.map(lead => renderLeadCard(lead, { hideProperty: true }))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
